/**
 * BusinessSignup.jsx — 5-step wizard for death-care business signup
 * (funeral homes, cemeteries, flower shops, priest / officiants, …).
 *
 * Wired to POST /api/ift/businesses. The visitor must already be
 * signed in via Firebase phone / email / Google — we send the signed
 * user's phone/email through as the admin identity.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Building2, MapPin, Phone, ShieldCheck, ArrowLeft, ArrowRight, Check } from "lucide-react";

const STEP_LABELS = ["Identity", "Business", "Location", "Contact", "Trust & docs"];

// Simplified group headings for the picker (backend enforces the enum).
const CATEGORY_GROUPS = [
  { label: "FUNERAL SERVICES", keys: ["funeral_home", "crematorium", "cemetery", "ash_scattering"] },
  { label: "PHYSICAL GOODS",   keys: ["flower_shop", "monument_headstone", "funeral_catering"] },
  { label: "TRANSPORT",         keys: ["limousine_service", "ash_transport"] },
  { label: "PROFESSIONAL",      keys: ["priest_officiant", "grief_counsellor", "memorial_photographer", "estate_lawyer", "final_expense_insurance", "genealogist"] },
];

const COMMON_SERVICES = [
  "cremation", "burial", "embalming", "transport", "flowers",
  "priest_arrangement", "catering", "photography", "grief_counselling",
  "ash_transport", "obituary_publication", "livestream",
];

export default function BusinessSignup() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [taxonomy, setTaxonomy] = useState(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    business_name: "",
    business_category: "funeral_home",
    business_subtypes: [],
    services_offered: [],
    primary_location: { address_line: "", city: "", state: "", country: "IN", postal_code: "", service_area_km: 25 },
    admin_email: "", admin_phone_e164: "",
    business_email: "", business_phone_e164: "", whatsapp_e164: "", website: "",
    gstin: "", years_in_business: "",
    about: "",
    hours: {
      monday: "09:00-18:00", tuesday: "09:00-18:00", wednesday: "09:00-18:00",
      thursday: "09:00-18:00", friday: "09:00-18:00", saturday: "09:00-18:00", sunday: "closed",
      emergency_available_24_7: true,
    },
  });

  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      admin_email: f.admin_email || user.email || "",
      admin_phone_e164: f.admin_phone_e164 || user.phone_number || "",
      whatsapp_e164: f.whatsapp_e164 || user.phone_number || "",
    }));
  }, [user]);

  useEffect(() => {
    http.get("/ift/business-taxonomy").then(({ data }) => setTaxonomy(data)).catch(() => setTaxonomy(null));
  }, []);

  const categoryLabel = useMemo(() => {
    if (!taxonomy) return form.business_category;
    return taxonomy.categories.find((c) => c.key === form.business_category)?.label || form.business_category;
  }, [taxonomy, form.business_category]);

  const canNext = useMemo(() => {
    switch (step) {
      case 0: return !!user;
      case 1: return form.business_name.trim().length >= 2 && !!form.business_category;
      case 2: return form.primary_location.city.trim() && form.primary_location.country.trim();
      case 3: return true;
      case 4: return true;
      default: return false;
    }
  }, [step, user, form]);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const patchLoc = (k, v) => setForm((f) => ({ ...f, primary_location: { ...f.primary_location, [k]: v } }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        years_in_business: form.years_in_business ? parseInt(form.years_in_business, 10) : null,
        gstin: form.gstin.trim().toUpperCase() || null,
        website: form.website.trim() || null,
        about: form.about.trim() || null,
        admin_email: form.admin_email.trim() || null,
        admin_phone_e164: form.admin_phone_e164.trim() || null,
        business_email: form.business_email.trim() || null,
        business_phone_e164: form.business_phone_e164.trim() || null,
        whatsapp_e164: form.whatsapp_e164.trim() || null,
      };
      const { data } = await http.post("/ift/businesses", payload);
      toast.success("Business listing created — welcome to IndianFamilyTree!");
      navigate(`/care/${data.business.business_slug}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (Array.isArray(detail)) toast.error(detail[0]?.msg || "Please review your entries");
      else toast.error(detail || "Could not create business");
    } finally { setBusy(false); }
  };

  if (loading) return null;

  if (!user) {
    return (
      <div className="max-w-md mx-auto py-16 px-6 text-center" data-testid="biz-signup-need-login">
        <Building2 className="w-8 h-8 mx-auto mb-3 opacity-60" />
        <h1 className="font-display text-2xl mb-2">Sign in first</h1>
        <p className="text-sm opacity-70 mb-4">
          You need an IndianFamilyTree account before listing your business. It only takes a phone OTP.
        </p>
        <Link to={`/login?next=/care/signup`} className="wofy-btn-primary text-sm">Sign in with phone</Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-10 px-4" data-testid="biz-signup">
      <div className="mb-6">
        <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>LIST YOUR BUSINESS ON IFT</div>
        <h1 className="font-display text-3xl">Serve families with dignity</h1>
        <p className="text-sm mt-2 opacity-70">
          Funeral homes, cemeteries, flower shops, priests, grief counsellors — join the IndianFamilyTree death-care marketplace.
        </p>
      </div>

      <ol className="flex items-center gap-2 mb-6 text-[10px] font-mono uppercase tracking-wider">
        {STEP_LABELS.map((label, i) => (
          <li
            key={label}
            className="flex-1 rounded-full px-2 py-1 text-center transition-colors"
            style={{
              background: i < step ? "#DCFCE7" : i === step ? "var(--wofy-primary)" : "var(--wofy-surface)",
              color: i === step ? "#fff" : (i < step ? "#166534" : "var(--wofy-text-soft)"),
            }}
            data-testid={`biz-signup-step-${i}`}
          >
            {i + 1}. {label}
          </li>
        ))}
      </ol>

      <div className="wofy-card space-y-4">
        {step === 0 && (
          <section className="space-y-2 text-sm" data-testid="biz-signup-step-content-0">
            <div className="flex items-center gap-2 rounded-lg p-3" style={{ background: "var(--wofy-surface)" }}>
              <ShieldCheck className="w-4 h-4" />
              <span>You're signed in as <b>{user.name || user.email || user.phone_number}</b>.</span>
            </div>
            <p className="opacity-70">
              We'll link this business to your account. Later you can invite a co-admin (spouse, sibling, employee) by email or phone.
            </p>
          </section>
        )}

        {step === 1 && (
          <section className="space-y-3" data-testid="biz-signup-step-content-1">
            <label className="block">
              <div className="overline mb-1">BUSINESS NAME</div>
              <input value={form.business_name} onChange={(e) => patch("business_name", e.target.value)} maxLength={200} className="w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-line)" }} data-testid="biz-signup-name" placeholder="e.g. Sadgati Funeral Services" />
            </label>
            <label className="block">
              <div className="overline mb-1">CATEGORY</div>
              <select value={form.business_category} onChange={(e) => patch("business_category", e.target.value)} className="w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-line)" }} data-testid="biz-signup-category">
                {CATEGORY_GROUPS.map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.keys.map((k) => <option key={k} value={k}>{taxonomy?.categories?.find((c) => c.key === k)?.label || k}</option>)}
                  </optgroup>
                ))}
                <option value="other">Other</option>
              </select>
            </label>
            <div>
              <div className="overline mb-1">FAITH / TRADITION (multi-select)</div>
              <div className="flex flex-wrap gap-2">
                {(taxonomy?.subtypes || []).map((s) => {
                  const active = form.business_subtypes.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => patch("business_subtypes", active ? form.business_subtypes.filter((x) => x !== s) : [...form.business_subtypes, s])}
                      data-testid={`biz-signup-subtype-${s}`}
                      className="text-xs px-2.5 py-1 rounded-full border capitalize transition-colors"
                      style={{
                        borderColor: active ? "var(--wofy-primary)" : "var(--wofy-line)",
                        background: active ? "var(--wofy-primary)" : "transparent",
                        color: active ? "#fff" : "var(--wofy-text)",
                      }}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="overline mb-1">SERVICES OFFERED</div>
              <div className="flex flex-wrap gap-2">
                {COMMON_SERVICES.map((s) => {
                  const active = form.services_offered.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => patch("services_offered", active ? form.services_offered.filter((x) => x !== s) : [...form.services_offered, s])}
                      data-testid={`biz-signup-service-${s}`}
                      className="text-xs px-2.5 py-1 rounded-full border transition-colors"
                      style={{
                        borderColor: active ? "var(--wofy-primary)" : "var(--wofy-line)",
                        background: active ? "var(--wofy-primary)" : "transparent",
                        color: active ? "#fff" : "var(--wofy-text)",
                      }}
                    >
                      {s.replace(/_/g, " ")}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {step === 2 && (
          <section className="space-y-3" data-testid="biz-signup-step-content-2">
            <label className="block">
              <div className="overline mb-1">STREET / ADDRESS LINE</div>
              <input value={form.primary_location.address_line} onChange={(e) => patchLoc("address_line", e.target.value)} className="w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-line)" }} data-testid="biz-signup-address" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><div className="overline mb-1">CITY *</div>
                <input value={form.primary_location.city} onChange={(e) => patchLoc("city", e.target.value)} required className="w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-line)" }} data-testid="biz-signup-city" /></label>
              <label className="block"><div className="overline mb-1">STATE</div>
                <input value={form.primary_location.state} onChange={(e) => patchLoc("state", e.target.value)} className="w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-line)" }} data-testid="biz-signup-state" /></label>
              <label className="block"><div className="overline mb-1">POSTAL / PIN</div>
                <input value={form.primary_location.postal_code} onChange={(e) => patchLoc("postal_code", e.target.value)} className="w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-line)" }} data-testid="biz-signup-postal" /></label>
              <label className="block"><div className="overline mb-1">COUNTRY *</div>
                <input value={form.primary_location.country} onChange={(e) => patchLoc("country", e.target.value)} className="w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-line)" }} data-testid="biz-signup-country" /></label>
            </div>
            <label className="block"><div className="overline mb-1">SERVICE AREA (KM)</div>
              <input type="number" min={1} max={500} value={form.primary_location.service_area_km} onChange={(e) => patchLoc("service_area_km", parseInt(e.target.value || "0", 10))} className="w-32 rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-line)" }} data-testid="biz-signup-area" /></label>
          </section>
        )}

        {step === 3 && (
          <section className="space-y-3" data-testid="biz-signup-step-content-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block"><div className="overline mb-1">BUSINESS PHONE</div>
                <input value={form.business_phone_e164} onChange={(e) => patch("business_phone_e164", e.target.value)} placeholder="+91…" className="w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-line)" }} data-testid="biz-signup-bphone" /></label>
              <label className="block"><div className="overline mb-1">WHATSAPP</div>
                <input value={form.whatsapp_e164} onChange={(e) => patch("whatsapp_e164", e.target.value)} placeholder="+91…" className="w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-line)" }} data-testid="biz-signup-wa" /></label>
              <label className="block"><div className="overline mb-1">BUSINESS EMAIL</div>
                <input value={form.business_email} onChange={(e) => patch("business_email", e.target.value)} className="w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-line)" }} data-testid="biz-signup-bemail" /></label>
              <label className="block"><div className="overline mb-1">WEBSITE</div>
                <input value={form.website} onChange={(e) => patch("website", e.target.value)} placeholder="https://…" className="w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-line)" }} data-testid="biz-signup-website" /></label>
            </div>
            <label className="flex items-start gap-2 text-sm mt-2">
              <input type="checkbox" checked={form.hours.emergency_available_24_7} onChange={(e) => patch("hours", { ...form.hours, emergency_available_24_7: e.target.checked })} data-testid="biz-signup-24-7" />
              <span>Available for emergencies 24/7 (recommended for funeral homes, transport, crematoriums)</span>
            </label>
          </section>
        )}

        {step === 4 && (
          <section className="space-y-3" data-testid="biz-signup-step-content-4">
            <p className="text-xs opacity-70">These verify your business faster. GSTIN unlocks the "documents submitted" trust tier automatically.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block"><div className="overline mb-1">GSTIN (India)</div>
                <input value={form.gstin} onChange={(e) => patch("gstin", e.target.value.toUpperCase())} placeholder="e.g. 27AAAAA0000A1Z5" maxLength={15} className="w-full rounded-lg border px-3 py-2 text-sm font-mono" style={{ borderColor: "var(--wofy-line)" }} data-testid="biz-signup-gstin" /></label>
              <label className="block"><div className="overline mb-1">YEARS IN BUSINESS</div>
                <input type="number" min={0} max={200} value={form.years_in_business} onChange={(e) => patch("years_in_business", e.target.value)} className="w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-line)" }} data-testid="biz-signup-years" /></label>
            </div>
            <label className="block">
              <div className="overline mb-1">ABOUT (family-friendly, no marketing puffery)</div>
              <textarea value={form.about} onChange={(e) => patch("about", e.target.value)} rows={4} maxLength={4000} className="w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-line)" }} placeholder="Family-owned funeral home serving Central Kerala since 2010. Multi-faith services with 24/7 emergency availability." data-testid="biz-signup-about" />
            </label>
            <div className="text-xs rounded-lg p-3 flex items-start gap-2" style={{ background: "var(--wofy-surface)", color: "var(--wofy-text-soft)" }}>
              <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Your listing starts in <b>pending review</b>. Upload your GST certificate or trade licence right after signup to advance to
                <b> Documents Submitted</b>. Our team then does a field verification (phone call or visit) to award the green
                <b> Verified</b> badge — families trust verified listings.
              </span>
            </div>
          </section>
        )}
      </div>

      <div className="flex justify-between mt-6">
        <button
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          className="wofy-btn-ghost text-sm disabled:opacity-40"
          data-testid="biz-signup-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        {step < STEP_LABELS.length - 1 ? (
          <button
            onClick={() => setStep((s) => s + 1)}
            disabled={!canNext}
            className="wofy-btn-primary text-sm"
            data-testid="biz-signup-next"
          >
            Next <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={submit}
            disabled={busy}
            className="wofy-btn-primary text-sm"
            data-testid="biz-signup-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {busy ? "Creating…" : "Create business listing"}
          </button>
        )}
      </div>
    </div>
  );
}
